import { useMemo } from 'react';

import { colors } from '@ui';
import * as THREE from 'three';

import { getKeypointId, getSafeSkeleton } from '../helpers';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';

const VISOR_DEPTH_RATIO = 0.2; // Depth of visor cut as fraction of radius
const VISOR_HEIGHT_RATIO = 0.1; // Vertical extent of visor (both +/- from center)
const EAR_RADIUS_RATIO = 0.7; // Ear disc radius as fraction of slice cutout radius
const EAR_DEPTH_RATIO = 0.05; // Ear disc thickness as fraction of head radius

// Sphere with flat ear slices and a visor cut
function createSlicedSphereGeometry(radius: number, sliceRatio = 1): THREE.BufferGeometry {
    const geometry = new THREE.SphereGeometry(radius, 128, 128);
    const positions = geometry.attributes['position'] as THREE.BufferAttribute;
    const normals = geometry.attributes['normal'] as THREE.BufferAttribute;

    const sliceY = radius * sliceRatio;
    const visorTop = radius * VISOR_HEIGHT_RATIO;
    const visorBottom = -radius * VISOR_HEIGHT_RATIO;
    const visorWidthLimit = sliceY;
    const visorPlaneX = radius * (1 - VISOR_DEPTH_RATIO);

    for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);

        // Flat ear slices
        if (y > sliceY) {
            positions.setY(i, sliceY);
            normals.setX(i, 0);
            normals.setY(i, 1);
            normals.setZ(i, 0);
        } else if (y < -sliceY) {
            positions.setY(i, -sliceY);
            normals.setX(i, 0);
            normals.setY(i, -1);
            normals.setZ(i, 0);
        }

        // Visor cut
        if (z < visorTop && z > visorBottom && Math.abs(y) < visorWidthLimit) {
            if (x > visorPlaneX) {
                positions.setX(i, visorPlaneX);
                normals.setX(i, 1);
                normals.setY(i, 0);
                normals.setZ(i, 0);
            }
        }
    }

    positions.needsUpdate = true;
    normals.needsUpdate = true;
    geometry.computeVertexNormals();

    return geometry;
}

type HeadMeshProps = {
    frame: number;
    skeleton: FrameFirstSkeleton;
    keypointIdToName: KeypointIdToName;
    isTranslucent?: boolean;
    isComparison?: boolean;
};

function useHeadGeometry(skeleton: FrameFirstSkeleton, frame: number, keypointIdToName: KeypointIdToName) {
    return useMemo(() => {
        const noseId = getKeypointId('nose', keypointIdToName);
        const leftEarId = getKeypointId('left_ear', keypointIdToName);
        const rightEarId = getKeypointId('right_ear', keypointIdToName);
        const headId = getKeypointId('head', keypointIdToName);

        const nose = getSafeSkeleton(skeleton, frame, noseId);
        const leftEar = getSafeSkeleton(skeleton, frame, leftEarId);
        const rightEar = getSafeSkeleton(skeleton, frame, rightEarId);
        const head = getSafeSkeleton(skeleton, frame, headId);

        if (!nose || !leftEar || !rightEar || !head) {
            return null;
        }

        const noseVec = new THREE.Vector3(...nose);
        const leftEarVec = new THREE.Vector3(...leftEar);
        const rightEarVec = new THREE.Vector3(...rightEar);
        const headVec = new THREE.Vector3(...head);

        const earToEarDistance = leftEarVec.distanceTo(rightEarVec);
        const earMidpoint = new THREE.Vector3().addVectors(leftEarVec, rightEarVec).multiplyScalar(0.5);
        const earToNoseDistance = earMidpoint.distanceTo(noseVec);
        const headRadius = Math.max(earToEarDistance, earToNoseDistance) * 0.5;
        const centerPosition = headVec.clone();

        // Compute orientation directly from keypoints
        const yDir = leftEarVec.clone().sub(rightEarVec).normalize(); // right -> left (lateral)
        const headToNose = noseVec.clone().sub(headVec); // head -> nose
        const zDir = new THREE.Vector3().crossVectors(headToNose, yDir).normalize(); // up
        const xDir = new THREE.Vector3().crossVectors(yDir, zDir).normalize(); // forward

        // Build rotation matrix from axes [xDir, yDir, zDir] as columns
        const rotationMatrix = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
        const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

        return { centerPosition, headRadius, quaternion };
    }, [skeleton, frame, keypointIdToName]);
}

const SLICE_RATIO = 0.8;

export const HeadMesh = ({
    frame,
    skeleton,
    keypointIdToName,
    isTranslucent = false,
    isComparison = false,
}: HeadMeshProps) => {
    const headGeometry = useHeadGeometry(skeleton, frame, keypointIdToName);

    const quaternion = headGeometry?.quaternion ?? new THREE.Quaternion();
    const headRadius = headGeometry?.headRadius ?? 0;
    const slicedGeometry = useMemo(() => createSlicedSphereGeometry(headRadius, SLICE_RATIO), [headRadius]);

    if (!headGeometry) {
        return null;
    }

    const { centerPosition } = headGeometry;

    const opacity = isComparison ? 0.5 : isTranslucent ? 0.25 : 1;
    const mainColor = isComparison ? colors.slate[400] : isTranslucent ? colors.slate[300] : colors.slate[50];
    const earColor = isComparison ? colors.slate[400] : isTranslucent ? colors.slate[300] : colors.slate[500];

    // Ear discs
    const sliceY = headRadius * SLICE_RATIO;
    const cutoutRadius = Math.sqrt(headRadius * headRadius - sliceY * sliceY);
    const earRadius = cutoutRadius * EAR_RADIUS_RATIO;
    const earDepth = headRadius * EAR_DEPTH_RATIO;

    // Visor interior dimensions
    const visorPlaneX = headRadius * (1 - VISOR_DEPTH_RATIO);
    const visorHeight = headRadius * VISOR_HEIGHT_RATIO * 2;
    const visorCrossRadius = Math.sqrt(headRadius * headRadius - visorPlaneX * visorPlaneX);
    const visorWidth = visorCrossRadius * 2;

    const visorColor = isComparison ? colors.slate[400] : isTranslucent ? colors.slate[300] : colors.slate[500];

    return (
        <group position={centerPosition} quaternion={quaternion}>
            <mesh geometry={slicedGeometry}>
                <meshStandardMaterial color={mainColor} flatShading transparent opacity={opacity} />
            </mesh>

            {/* Visor interior */}
            <mesh position={[visorPlaneX - 0.001, 0, 0]}>
                <boxGeometry args={[earDepth, visorWidth, visorHeight]} />
                <meshStandardMaterial color={visorColor} flatShading transparent opacity={opacity} />
            </mesh>

            {/* Right ear disc */}
            <mesh position={[0, -sliceY - earDepth / 2, 0]} rotation={[Math.PI / 2, Math.PI / 2, Math.PI / 2]}>
                <cylinderGeometry args={[earRadius, earRadius, earDepth, 16]} />
                <meshStandardMaterial color={earColor} flatShading transparent opacity={opacity} />
            </mesh>

            {/* Left ear disc */}
            <mesh position={[0, sliceY + earDepth / 2, 0]} rotation={[Math.PI / 2, Math.PI / 2, Math.PI / 2]}>
                <cylinderGeometry args={[earRadius, earRadius, earDepth, 16]} />
                <meshStandardMaterial color={earColor} flatShading transparent opacity={opacity} />
            </mesh>
        </group>
    );
};
